import React, { PureComponent } from 'react';
import {
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  AccordionSummary,
  Paper,
  TextField,
  Select,
  FormControl,
  FormControlLabel,
  MenuItem,
  Typography,
  LinearProgress,
  Slider,
  Tooltip,
} from '@mui/material';
import MyAccordion from '../utils/MyAccordion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LuRefreshCcwDot } from 'react-icons/lu';
import RefreshIcon from '@mui/icons-material/Refresh';
import { crmBase } from '../../airtable_crm';
import { equipmentBaseDefaultFields, equipmentBase } from '../../airtable_equipment';
import { debounce } from 'lodash';
import { generateColor, generateScheduledKey, robotsSortingFunction, updateState } from '../../utils';
import { alertError, alertWarn } from '../../alertDispatcher';
import { getCurrentSession } from '../../dataModelHelpers';
import JobAndDropoffList from './JobAndDropoffList';
import DropoffList from './DropoffList';
import { CustomCheckbox } from '../utils/CustomCheckbox';
import { LocalAtRecord, Job } from '../../db';
import { ScheduleMap, SchedulingRecordTypes } from '../../types/types';
import EventBus from '../../EventBus';
import _ from 'lodash';
import type {
  DropoffsFieldSet,
  JobsFieldSet,
  RigsFieldSet,
  RobotsFieldSet,
  ShiftDropoffsFieldSet,
  ShiftsFieldSet,
  TeamFieldSet,
  FieldSet,
} from '@rogoag/airtable/crm';
import ShiftRolloverForm from './ShiftRolloverForm';
import CreateShift from './CreateShift';
import { createShiftInAt } from './helpers/shift';
import SchedulingGrouped from './SchedulingGrouped';
import { createLocalAtRecordByAtModel } from '../../db_ops/airtable_record_ops';
import { getLocalAtSearch } from '../../db_ops/airtable_search_ops';
import { Equipment, EquipmentField } from '@rogoag/airtable/equipment';

interface SchedulingListProps {
  scheduled: ScheduleMap;
  filtered: LocalAtRecord[];
  robotRefreshRate: number;
  showDropoffs: boolean;
  showRobots: boolean;
  showTrucks: boolean;
  showJobs: boolean;
  selectedShift: string;
  loading: boolean;
  hiddenScheduled: string[];
  unassigned: LocalAtRecord[];
  shiftDropoffs: LocalAtRecord<ShiftDropoffsFieldSet>[];
  allRobots: LocalAtRecord<RobotsFieldSet>[];
  allTrucks: Equipment[];
  allRigs: LocalAtRecord<RigsFieldSet>[];
  allDropoffs: LocalAtRecord[];
  availableShifts: LocalAtRecord[];
  toggleShowJobs: () => void;
  toggleShowDropoffs: () => void;
  toggleShowRobots: () => void;
  toggleShowTrucks: () => void;
  updateZoomField: (zoomField: LocalAtRecord) => void;
  updateScheduled: (scheduled: ScheduleMap) => void;
  updateUnassigned: (unassigned: LocalAtRecord[]) => void;
  updateDisplayFilter: (filtered: LocalAtRecord[]) => void;
  processFieldBoundaries: (unassigned: LocalAtRecord[], scheduled: ScheduleMap) => Promise<void>;
  updateLoading: (loading: boolean) => void;
  updateAvailableShifts: (shifts: LocalAtRecord<ShiftsFieldSet>[]) => void;
  updateShiftDropoffs: (dropoffs: LocalAtRecord<ShiftDropoffsFieldSet>[]) => void;
  updateAllRobots: (robots: LocalAtRecord<RobotsFieldSet>[]) => void;
  updateAllTrucks: (trucks: Equipment[]) => void;
  updateAllRigs: (rigs: LocalAtRecord<RigsFieldSet>[]) => void;
  updateAllDropoffs: (dropoffs: LocalAtRecord<DropoffsFieldSet>[]) => void;
  updateHiddenScheduled: (hidden: string[]) => void;
  updateRobotRefreshRate: (rate: number) => void;
  updateSelectedShift: (shift: string) => void;
}

type FilterByLate = '<' | '>' | '==';
type FilterName = 'contains' | '!contains';

const groupScheduledByOptions = [
  { value: 'Operator', label: 'Operator' },
  { value: 'Sched Date', label: 'Sched Date' },
] as const;

type GroupScheduledBy = (typeof groupScheduledByOptions)[number]['value'];

interface SchedulingListState {
  filterName: FilterName;
  filterByLate: FilterByLate;
  filterByPriority: string;
  filterTextName: string;
  nameFilter: boolean;
  dateFilter: boolean;
  lateFilter: boolean;
  usePriorityFilter: boolean;
  filterTextLate: string;
  isResizing: boolean;
  lastDownX: number;
  width: number;
  allOperators: LocalAtRecord<TeamFieldSet>[];
  robot: string;
  rig: string;
  operator: string;
  schedDate: string;
  nameFilterDropoff: boolean;
  dropoffContainsFilter: FilterName;
  filterDropoffName: string;
  groupScheduledBy: GroupScheduledBy;
  rolloverModalOpen: boolean;
  jobRolloverFrom: LocalAtRecord<JobsFieldSet> | undefined;
  availableShiftsByKeys: Record<string, LocalAtRecord<ShiftsFieldSet>>;
  // Lab name
  labNameFilterText: string;
  labNameFilterType: FilterName;
  labNameFilterEnabled: boolean;
  // Test Package
  testPackageFilterText: string;
  testPackageFilterType: FilterName;
  testPackageFilterEnabled: boolean;
}

const WAITING_ROBOT_NAME = 'WAITING';

export default class SchedulingList extends PureComponent<SchedulingListProps, SchedulingListState> {
  private _isMounted = false;
  truckRefreshInterval: NodeJS.Timeout;
  robotRefreshInterval: NodeJS.Timeout;
  prevRefreshRate: number;
  private handleMousemoveBound: (e: MouseEvent) => void;
  private handleMouseupBound: () => void;

  FILTER_KEYS = [
    'dateFilter',
    'nameFilter',
    'lateFilter',
    'filterByLate',
    'filterName',
    'filterTextLate',
    'filterTextName',
    'filterDropoffName',
    'dropoffContainsFilter',
    'nameFilterDropoff',
    'usePriorityFilter',
    'filterByPriority',
    // Lab name
    'labNameFilterText',
    'labNameFilterType',
    'labNameFilterEnabled',
    // Test Package
    'testPackageFilterText',
    'testPackageFilterType',
    'testPackageFilterEnabled',
  ];

  // SCHEDULE_VIEW_NAME = 'SERG - Scheduling test';
  SCHEDULE_VIEW_NAME = 'App View - Schedule Jobs';
  DROPOFF_VIEW_NAME = 'App View - Scheduled Dropoffs';
  container: React.RefObject<HTMLDivElement>;

  constructor(props: SchedulingListProps) {
    super(props);
    const prevWidth = localStorage.getItem('scheduleWidth');
    this.state = {
      filterName: 'contains',
      filterByLate: '==',
      filterTextName: '',
      filterByPriority: '',
      nameFilter: false,
      dateFilter: false,
      lateFilter: false,
      usePriorityFilter: false,
      filterTextLate: '',
      isResizing: false,
      lastDownX: 0,
      width: prevWidth ? parseInt(prevWidth) : 400,
      allOperators: [],
      robot: '',
      rig: '',
      operator: '',
      schedDate: '',
      nameFilterDropoff: false,
      dropoffContainsFilter: 'contains',
      filterDropoffName: '',
      groupScheduledBy: 'Sched Date',
      rolloverModalOpen: false,
      jobRolloverFrom: undefined,
      availableShiftsByKeys: {},
      // Lab name
      labNameFilterText: '',
      labNameFilterType: 'contains',
      labNameFilterEnabled: false,
      // Test package
      testPackageFilterText: '',
      testPackageFilterType: 'contains',
      testPackageFilterEnabled: false,
    };
    this.container = React.createRef();

    this.loadRobots = this.loadRobots.bind(this);
    this.loadTrucks = this.loadTrucks.bind(this);
    this.loadRigs = this.loadRigs.bind(this);
    this.filterByLabName = this.filterByLabName.bind(this);
    this.filterByTestPackage = this.filterByTestPackage.bind(this);
    this.startLiveTruckInterval = this.startLiveTruckInterval.bind(this);
    this.clearTruckInterval = this.clearTruckInterval.bind(this);
    this.startLiveRobotInterval = this.startLiveRobotInterval.bind(this);
    this.clearRobotInterval = this.clearRobotInterval.bind(this);
    this.refreshEverything = this.refreshEverything.bind(this);
    this.refreshJobsAndShifts = this.refreshJobsAndShifts.bind(this);
    this.handleMousemoveBound = (e: MouseEvent) => this.handleMousemove(e);
    this.handleMouseupBound = () => this.handleMouseup();
  }

  async componentDidMount() {
    this._isMounted = true;
    await this.refreshEverything();
    EventBus.on('SCHEDULING:REFRESH_JOBS', this.refreshEverything);
    document.addEventListener('mousemove', this.handleMousemoveBound);
    document.addEventListener('mouseup', this.handleMouseupBound);

    this.startLiveTruckInterval();
    this.startLiveRobotInterval();
  }

  componentWillUnmount(): void {
    this._isMounted = false;
    EventBus.remove('SCHEDULING:REFRESH_JOBS', this.refreshEverything);
    document.removeEventListener('mousemove', this.handleMousemoveBound);
    document.removeEventListener('mouseup', this.handleMouseupBound);

    this.clearTruckInterval();
    this.clearRobotInterval();
  }

  componentDidUpdate(prevProps: SchedulingListProps, prevState: SchedulingListState) {
    this.FILTER_KEYS.forEach((key) => {
      if (this.state[key] !== prevState[key] && prevState) {
        this.applyFilters();
      }
    });

    if (this.props.showDropoffs !== prevProps.showDropoffs) {
      this.applyFilters();
    }

    if (this.props.showJobs !== prevProps.showJobs) {
      this.applyFilters();
    }

    if (
      JSON.stringify(this.props.unassigned) !== JSON.stringify(prevProps.unassigned) ||
      JSON.stringify(this.props.scheduled) !== JSON.stringify(prevProps.scheduled)
    ) {
      this.applyFilters();
    }
  }

  async queryJobsTable(forceRefresh: boolean, viewName: string, query?: string) {
    try {
      const search = await getLocalAtSearch<JobsFieldSet>('Jobs', viewName, query, false);

      if (forceRefresh || search.getRecords().length === 0) {
        try {
          await search.refresh({ waitForAttachments: false });
        } catch (err) {
          console.error(`Could not refresh airtable Jobs.`, err);

          return undefined;
        }
      }

      return search.getRecords();
    } catch (e) {
      console.error(e);

      return undefined;
    }
  }

  async refreshEverything() {
    const session = getCurrentSession();

    if (!session) {
      return;
    }

    this.props.updateLoading(true);
    const jobs = await this.queryJobsTable(true, this.SCHEDULE_VIEW_NAME);
    const shiftIdsFromJobs = jobs
      ?.map((j) => j.getFirst('Shift') as string | undefined)
      .filter((id): id is string => Boolean(id));

    // these are just individual AirTable queries and thus could be executed in parallel...
    try {
      await Promise.all([
        this.loadShifts(shiftIdsFromJobs ? Array.from(new Set<string>(shiftIdsFromJobs)) : []),
        this.loadShiftDropoffs(),
        this.loadOperators(),
        this.loadRobots(),
        this.loadTrucks(),
        this.loadRigs(),
        this.loadDropoffLocations(),
      ]);
    } catch (e) {
      console.error(e);
      alertWarn('Error occurred while refreshing everything');

      this.props.updateLoading(false);

      return;
    }

    if (jobs) {
      await this.structureJobs(jobs); // structure jobs for processing
    }

    this.applyFilters();
    this.props.updateLoading(false);
  }

  async refreshJobsAndShifts() {
    const session = getCurrentSession();

    if (!session) {
      return;
    }

    this.props.updateLoading(true);
    const jobs = await this.queryJobsTable(true, this.SCHEDULE_VIEW_NAME);
    const shiftIdsFromJobs = jobs
      ?.map((j) => j.getFirst('Shift') as string | undefined)
      .filter((id): id is string => Boolean(id));

    // these are just individual AirTable queries and thus could be executed in parallel...
    try {
      await Promise.all([
        this.loadShifts(shiftIdsFromJobs ? Array.from(new Set<string>(shiftIdsFromJobs)) : []),
        this.loadShiftDropoffs(),
      ]);
    } catch (e) {
      alertWarn('Error occurred while refreshing jobs and shifts');

      this.props.updateLoading(false);

      return;
    }

    if (jobs) {
      await this.structureJobs(jobs); // structure jobs for processing
    }

    this.applyFilters();
    this.props.updateLoading(false);
  }

  async loadShiftDropoffs() {
    const search = await getLocalAtSearch<ShiftDropoffsFieldSet>('Shift Dropoffs', this.DROPOFF_VIEW_NAME);
    await search.refresh();
    const shiftDropoffs = search.getRecords();
    this.props.updateShiftDropoffs(shiftDropoffs);
  }

  async loadShifts(shiftIdsFromJobs: string[]) {
    const search = await getLocalAtSearch<ShiftsFieldSet>('Shifts', 'App View - Valid Schedule Shifts');
    await search.refresh();

    let availableShifts = search.getRecords();
    const availableShiftsIds = availableShifts.map((s) => s.id);

    const missingShiftIds: string[] = [];
    shiftIdsFromJobs.forEach((shiftId) => {
      if (shiftId && !availableShiftsIds.includes(shiftId)) {
        missingShiftIds.push(shiftId);
      }
    });

    if (missingShiftIds.length) {
      // Before refactor: Airtable.getRecordsByIds (which would call .refresh())
      const missingShifts = await crmBase.shifts.get(missingShiftIds);
      const missingShiftLocalRecords = missingShifts.map(createLocalAtRecordByAtModel);

      availableShifts = availableShifts.concat(missingShiftLocalRecords);
    }

    this.props.updateAvailableShifts(availableShifts);
    const waitingShifts = availableShifts.filter(
      (shift: LocalAtRecord<ShiftsFieldSet>) => shift.getFirst('Robot') === WAITING_ROBOT_NAME,
    );

    if (!waitingShifts.length) {
      return;
    }

    // Hide waiting shifts
    const hiddenScheduled = this.props.hiddenScheduled.slice();

    waitingShifts.map((shift) => {
      const robot = shift.get('Robot')?.[0];
      const schedDate = shift.get('Sched Date');
      const operatorName = shift.get('Operator Name')?.[0];
      const rig = shift.get('Rig #') || '';

      if (!robot || !schedDate || !operatorName || !rig) {
        return;
      }

      const key = generateScheduledKey(schedDate, robot, operatorName, rig);

      if (hiddenScheduled.includes(key)) {
        return;
      }

      hiddenScheduled.push(key);
    });

    this.props.updateHiddenScheduled(hiddenScheduled);
  }

  async loadDropoffLocations() {
    const search = await getLocalAtSearch<DropoffsFieldSet>('Dropoffs', '', '{Active} = TRUE()');
    await search.refresh();
    const allDropoffs = search.getRecords();
    this.props.updateAllDropoffs(allDropoffs);
  }

  async loadRobots() {
    const search = await getLocalAtSearch<RobotsFieldSet>('Robot');
    await search.refresh();

    let allRobots = search.getRecords();
    allRobots = allRobots.filter((robot) => robot.get('Name')?.includes('K')); // Only get Kubotas
    allRobots = allRobots.sort((a, b) => {
      const aName = a.get('Name');
      const bName = b.get('Name');

      return robotsSortingFunction(aName, bName);
    });

    const robotColorMap = {};
    allRobots.forEach((robot) => {
      const robotName = robot.get('Name') ?? '';
      robotColorMap[robotName] = generateColor(robotName);
    });

    this.props.updateAllRobots(allRobots);
  }

  async loadTrucks() {
    let allTrucks: Equipment[];

    try {
      allTrucks = await equipmentBase.equipment.get({
        formula: '{ID Prefix #noedit (from Type)} = "RIG"',
        fields: equipmentBaseDefaultFields['Equipment'] as EquipmentField[],
      });
    } catch (e) {
      console.error('Error fetching trucks');

      return;
    }

    allTrucks = allTrucks.sort((a, b) => {
      const aName = a.equipment;
      const bName = b.equipment;

      if (typeof aName === 'undefined') {
        return 1;
      }

      if (typeof bName === 'undefined') {
        return -1;
      }

      return Number(aName.replace(/\D/g, '')) - Number(bName.replace(/\D/g, ''));
    });

    this.props.updateAllTrucks(allTrucks);
  }

  async loadRigs() {
    const search = await getLocalAtSearch<RigsFieldSet>('Rigs', '', 'SEARCH("RIG-", {Name})');
    await search.refresh();

    let allRigs = search.getRecords();
    allRigs = allRigs.sort((a, b) => {
      const aName = a.get('Name');
      const bName = b.get('Name');

      if (typeof aName === 'undefined') {
        return 1;
      }

      if (typeof bName === 'undefined') {
        return -1;
      }

      return Number(aName.replace(/\D/g, '')) - Number(bName.replace(/\D/g, ''));
    });

    this.props.updateAllRigs(allRigs);
  }

  /**
   * Starts the live truck update
   */
  startLiveTruckInterval() {
    this.truckRefreshInterval = setInterval(async () => {
      await this.loadTrucks();
    }, 120 * 1000); // 2 minutes
  }

  clearTruckInterval() {
    clearInterval(this.truckRefreshInterval);
  }

  /**
   * Starts the live robot interval
   */
  startLiveRobotInterval() {
    this.prevRefreshRate = this.props.robotRefreshRate;
    this.robotRefreshInterval = setInterval(async () => {
      await this.loadRobots();

      if (this.props.robotRefreshRate !== this.prevRefreshRate) {
        this.clearRobotInterval();
        this.startLiveRobotInterval();
      }
    }, this.props.robotRefreshRate * 1000);
  }

  clearRobotInterval() {
    clearInterval(this.robotRefreshInterval);
  }

  async loadOperators() {
    const search = await getLocalAtSearch<TeamFieldSet>('Team', 'Valid Operators');
    await search.refresh();
    const allOperators = search.getRecords().sort((a, b) => a.get('Name')!.localeCompare(b.get('Name')!));

    if (this._isMounted) {
      this.setState({ allOperators });
    }
  }

  async createShift() {
    const selectedOperator = this.state.operator;
    const selectedRobot = this.state.robot;
    const selectedRig = this.state.rig;
    const schedDate = this.state.schedDate;

    if (!selectedOperator || !schedDate || !selectedRobot || !selectedRig) {
      alertError('Did not input all fields');

      return;
    }

    const robotObj = this.props.allRobots.find((robot) => robot.id === selectedRobot);
    const operatorObj = this.state.allOperators.find((operator) => operator.id === selectedOperator);
    const rigObj = this.props.allRigs.find((rig) => rig.id === selectedRig);

    if (!robotObj || !operatorObj || !rigObj) {
      alertError(`Could not find required objects in the storage, aborting`);

      return;
    }

    const robotName = robotObj.get('Name');
    const operatorName = operatorObj.get('Name');
    const rigName = rigObj.get('Name');

    if (!robotName || !operatorName || !rigName) {
      alertError('Shift parameters are wrong, aboring');

      return;
    }

    const newShift = await createShiftInAt(this.state.schedDate, this.state.robot, this.state.operator, this.state.rig);

    if (!newShift) {
      alertError('Failed to create a shift');

      return;
    }

    const key = generateScheduledKey(schedDate, robotName, operatorName, rigName);
    this.props.scheduled[key] = [];

    if (this._isMounted) {
      this.setState((prevState) => ({
        availableShiftsByKeys: {
          ...prevState.availableShiftsByKeys,
          [key]: newShift,
        },
      }));
    }

    const availableShifts = this.props.availableShifts.slice();
    availableShifts.push(newShift);
    this.props.updateAvailableShifts(availableShifts);
    this.props.updateScheduled(this.props.scheduled);

    if (this._isMounted) {
      this.setState({ schedDate: '', operator: '', robot: '', rig: '' });
    }
  }

  handleJobs(jobs: LocalAtRecord<JobsFieldSet>[], scheduled: ScheduleMap, unassigned: LocalAtRecord[]) {
    let skippedJobs = 0;
    jobs.forEach((job) => {
      const isScheduled = job.get('Scheduled?');

      if (isScheduled) {
        // if assigned, we need to organize it by robot and schedule date
        const robot = job.get('Robot');
        const schedDate = job.get('Sched Date')?.[0];
        const operatorName = job.get('Operator Name')?.[0];
        const rig = job.get('Rig #')?.[0];

        if (!robot || !schedDate || !operatorName || !rig) {
          skippedJobs++;

          return;
        }

        const key = generateScheduledKey(schedDate, robot, operatorName, rig);

        if (key in scheduled) {
          scheduled[key].push(job as SchedulingRecordTypes);
        } else {
          skippedJobs++;
          console.error(`KEY ${key} NOT IN Scheduled, job ${job.id} - ${job.get('Name')} skipped`);
        }
      } else {
        unassigned.push(job); // if unassigned no structure needed
      }
    });

    if (skippedJobs > 0) {
      alertError(`${skippedJobs} jobs were skipped. Possible reasons: missing required fields or invalid shift.`);
    }

    return { scheduled, unassigned };
  }

  handleShiftDropoffs(scheduled: ScheduleMap, unassigned) {
    this.props.shiftDropoffs.forEach((dropoff) => {
      const robot = dropoff.getFirst('Robot') || '';
      const schedDate = dropoff.getFirst('Sched Date') || '';
      const operatorName = dropoff.getFirst('Operator Name') || '';
      const rigs = dropoff.get('Rig # (from Shift) string');
      const rig = Array.isArray(rigs) ? rigs[0] || '' : '';

      if (!robot || !schedDate || !operatorName || !rig) {
        return;
      }

      const key = generateScheduledKey(schedDate, robot, operatorName, rig);

      if (scheduled[key]) {
        scheduled[key].push(dropoff);
      }
    });

    return { scheduled: scheduled, unassigned: unassigned };
  }

  handleShifts() {
    const scheduled: ScheduleMap = {};
    const byKeys = {};
    this.props.availableShifts.forEach((shift) => {
      // lookups/links return arrays
      const robot = shift.get('Robot')?.[0];
      const schedDate = shift.get('Sched Date');
      const operatorName = shift.get('Operator Name')?.[0];
      const rig = shift.get('Rig #') || '';

      if (!robot || !schedDate || !operatorName || !rig) {
        return;
      }

      const key = generateScheduledKey(schedDate, robot, operatorName, rig);
      scheduled[key] = [];
      byKeys[key] = shift;
    });

    if (this._isMounted) {
      this.setState({ availableShiftsByKeys: byKeys });
    }

    return scheduled;
  }

  async structureJobs(jobs: LocalAtRecord<JobsFieldSet>[]) {
    let unassigned: LocalAtRecord[] = [];
    let scheduled = this.handleShifts(); // initialize the possible shifts for jobs
    const jobsAddedObj = this.handleJobs(jobs, scheduled, unassigned);
    scheduled = jobsAddedObj.scheduled;
    unassigned = jobsAddedObj.unassigned;
    // We pull shift dropoffs but we only want to show them if the shift is within the valid range
    const shiftDropoffsAdded = this.handleShiftDropoffs(scheduled, unassigned);
    scheduled = shiftDropoffsAdded.scheduled;
    unassigned = shiftDropoffsAdded.unassigned;
    // we want dropoffs(not shift dropoffs) to be apart of the unassigned array
    unassigned = unassigned.concat(this.props.allDropoffs);
    // Airtable does not pull in order, we have to order it
    Object.keys(scheduled).forEach((key) => {
      scheduled[key] = scheduled[key].sort((a, b) => {
        const aOrder = a.get('Order');
        const bOrder = b.get('Order');

        if (typeof aOrder === 'undefined') {
          return 1;
        }

        if (typeof bOrder === 'undefined') {
          return -1;
        }

        return (aOrder as number) - (bOrder as number);
      });
    });

    await this.props.processFieldBoundaries(unassigned, scheduled); // kick off processing boundaries in the db worker
    this.props.updateUnassigned(unassigned);

    console.time('UPDATE SCHEDULED');
    this.props.updateScheduled(scheduled);
    console.timeEnd('UPDATE SCHEDULED');
  }

  filterScheduled(scheduled, filterFunc) {
    for (const key of scheduled.keys()) {
      scheduled.set(key, filterFunc(scheduled[key]));
    }

    return scheduled;
  }

  filterName(filterText: string, filterContains: FilterName, unassigned: SchedulingRecordTypes[], table: string) {
    filterText = filterText.toLowerCase();
    const contains = filterContains === 'contains';

    if (contains) {
      unassigned = unassigned.filter((job) => {
        if (job.table !== table) {
          return true;
        }

        const lowercaseName = job.get('Name')?.toLowerCase() || '';

        return lowercaseName.includes(filterText);
      });
    } else {
      unassigned = unassigned.filter((job) => {
        if (job.table !== table) {
          return true;
        }

        const lowercaseName = job.get('Name')?.toLowerCase() || '';

        return !lowercaseName.includes(filterText);
      });
    }

    return unassigned;
  }

  filterByLabName(records: SchedulingRecordTypes[]) {
    const filterText = this.state.labNameFilterText.toLowerCase();
    const contains = this.state.labNameFilterType === 'contains';

    records = records.filter((record) => {
      if (record.table === 'Jobs') {
        const lowercaseName = ((record.get('Lab - Final') as string | undefined) || '').toLowerCase();

        const found = lowercaseName.includes(filterText);

        return contains ? found : !found;
      } else {
        return contains;
      }
    });

    return records;
  }

  filterByTestPackage(records: SchedulingRecordTypes[]) {
    const filterText = this.state.labNameFilterText.toLowerCase();
    const contains = this.state.labNameFilterType === 'contains';

    records = records.filter((record) => {
      if (record.table === 'Jobs') {
        const lowercaseName = ((record.get('Test Pckg - Final') as string | undefined) || '').toLowerCase();

        const found = lowercaseName.includes(filterText);

        return contains ? found : !found;
      } else {
        return contains;
      }
    });

    return records;
  }

  filterByPriority(filterText: string, unassigned: LocalAtRecord<FieldSet>[]) {
    unassigned = unassigned.filter((job) => {
      if (job.table !== 'Jobs') {
        return true;
      }

      return job.get('Priority OVRD') === filterText;
    });

    return unassigned;
  }

  filterLate(filterText: string, filterByLate: FilterByLate, unassigned: SchedulingRecordTypes[], table: string) {
    if (filterByLate === '==') {
      unassigned = unassigned.filter((job) => {
        if (job.table !== table) {
          return true;
        }

        return job.get('Late (days)') === parseInt(filterText);
      });
    } else if (filterByLate === '>') {
      unassigned = unassigned.filter((job) => {
        if (job.table !== table) {
          return true;
        }

        return (job.get('Late (days)') as number) > parseInt(filterText);
      });
    } else {
      unassigned = unassigned.filter((job) => {
        if (job.table !== table) {
          return true;
        }

        return (job.get('Late (days)') as number) < parseInt(filterText);
      });
    }

    return unassigned;
  }

  async updateFilter(stateKey: keyof SchedulingListState, value: string | boolean) {
    this.setState(updateState(stateKey, value));
    //this.setState({ [stateKey]: value });
  }

  applyFilters() {
    let unassigned = this.props.unassigned.slice();

    if (this.state.nameFilter && this.state.filterTextName) {
      unassigned = this.filterName(this.state.filterTextName, this.state.filterName, unassigned, 'Jobs');
    }

    if (this.state.nameFilterDropoff && this.state.filterDropoffName) {
      unassigned = this.filterName(
        this.state.filterDropoffName,
        this.state.dropoffContainsFilter,
        unassigned,
        'Dropoffs',
      );
    }

    if (this.state.lateFilter && this.state.filterTextLate) {
      unassigned = this.filterLate(this.state.filterTextLate, this.state.filterByLate, unassigned, 'Jobs');
    }

    if (this.state.usePriorityFilter && this.state.filterByPriority) {
      unassigned = this.filterByPriority(this.state.filterByPriority, unassigned);
    }

    if (this.state.labNameFilterEnabled && this.state.labNameFilterText) {
      unassigned = this.filterByLabName(unassigned);
    }

    if (this.props.showJobs && this.state.testPackageFilterEnabled && this.state.testPackageFilterText) {
      unassigned = this.filterByTestPackage(unassigned);
    }

    if (!this.props.showDropoffs) {
      unassigned = unassigned.filter((job) => job.table !== 'Dropoffs');
    }

    if (!this.props.showJobs) {
      unassigned = unassigned.filter((job) => job.table !== 'Jobs');
    }

    this.props.updateDisplayFilter(unassigned);
  }

  handleMousedown(e: React.MouseEvent<HTMLDivElement>) {
    this.setState({ isResizing: true, lastDownX: e.clientX });
  }

  handleMousemove(e: MouseEvent) {
    // we don't want to do anything if we aren't resizing.
    if (!this._isMounted || !this.state.isResizing) {
      return;
    }

    const maxWidth = 1500;
    const minWidth = 0;
    const threshold = 10;

    if (
      e.x < maxWidth &&
      e.x > minWidth &&
      (e.x - threshold < this.state.width || e.x + threshold > this.state.width)
    ) {
      this.setState({ width: e.x });
    }
  }

  handleMouseup() {
    if (!this._isMounted) {
      return;
    }

    this.setState({ isResizing: false });
    localStorage.setItem('scheduleWidth', this.state.width.toString());
  }

  sortObject(obj) {
    return Object.keys(obj)
      .sort()
      .reduce(function (result, key) {
        result[key] = obj[key];

        return result;
      }, {});
  }

  groupScheduled(groupBy: GroupScheduledBy): Record<string, Record<string, SchedulingRecordTypes[]>> {
    const scheduled = this.props.scheduled;

    return Object.keys(scheduled).reduce(
      (acc: Record<string, Record<string, SchedulingRecordTypes[]>>, key: string) => {
        const items = scheduled[key];

        const [date, robot, operator, rig] = key.split('/');
        const groupByKey = groupBy === 'Sched Date' ? date : operator; // Change this to group by operator if needed

        if (!acc[groupByKey]) {
          acc[groupByKey] = {};
        }

        if (!acc[groupByKey][key]) {
          acc[groupByKey][key] = [];
        }

        acc[groupByKey][key].push(...items);

        return acc;
      },
      {},
    );
  }

  render() {
    let unassigned = this.props.filtered;
    let scheduled = { ...this.props.scheduled };
    scheduled = this.sortObject(scheduled);
    const scheduledGrouped = this.groupScheduled(this.state.groupScheduledBy);

    const dropoffs = unassigned.filter((job) => job.table === 'Dropoffs');
    unassigned = unassigned.filter((job) => job.table === 'Jobs');

    let scheduledCount = 0;
    Object.keys(scheduled).forEach((key) => {
      scheduledCount += scheduled[key].length;
    });

    let totalOpsAcresUnassigned = 0;
    let totalBoundaryAcresUnassigned = 0;
    unassigned.forEach((job) => {
      totalOpsAcresUnassigned += Job.getOpsAcresFromAtJob(job);
      totalBoundaryAcresUnassigned += Job.getBoundaryAcresFromAtJob(job);
    });

    return (
      <div
        style={{
          height: '100%',
          minHeight: 0,
          bottom: '0px',
          maxWidth: this.state.width,
        }}
        ref={this.container}
      >
        <Grid
          style={{
            flexDirection: 'row',
            display: 'flex',
            flex: 1,
            height: '100%',
            minHeight: 0,
          }}
        >
          <Paper
            elevation={3}
            style={{ maxHeight: 'calc(100vh - 88px)', minHeight: '100%', overflowY: 'scroll' }}
            className={'scroll'}
          >
            {this.props.loading ? <LinearProgress style={{ marginBottom: 20 }} /> : null}

            <Grid container style={{ flexDirection: 'row', display: 'flex' }} size={12}>
              <Grid
                container
                justifyContent="space-between"
                style={{ display: 'flex', marginRight: 20, marginTop: 10 }}
                size={{
                  md: 12,
                }}
              >
                <Tooltip title={'Refresh everything'}>
                  <span>
                    <IconButton
                      color="primary"
                      onClick={this.refreshEverything}
                      size="small"
                      disabled={this.props.loading}
                    >
                      <LuRefreshCcwDot />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title={'Refresh jobs and shifts'}>
                  <span>
                    <IconButton
                      color="primary"
                      onClick={this.refreshJobsAndShifts}
                      size="small"
                      disabled={this.props.loading}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>

            <Grid
              container
              style={{ flexDirection: 'row', display: 'flex', paddingBottom: 10 }}
              justifyContent="center"
              size={12}
            >
              <FormControl>
                <FormControlLabel
                  label="Show Jobs"
                  control={
                    <CustomCheckbox
                      onChange={() => this.props.toggleShowJobs()}
                      checked={this.props.showJobs}
                      style={{ marginLeft: 2.5 }}
                    />
                  }
                />
              </FormControl>

              <FormControl>
                <FormControlLabel
                  label="Show Dropoffs"
                  control={<CustomCheckbox onChange={this.props.toggleShowDropoffs} style={{ marginLeft: 2.5 }} />}
                />
              </FormControl>
            </Grid>

            <Grid
              container
              style={{ flexDirection: 'row', display: 'flex', paddingBottom: 10 }}
              justifyContent="center"
              size={12}
            >
              <FormControl>
                <FormControlLabel
                  label="Show Robots"
                  control={
                    <CustomCheckbox
                      onChange={() => this.props.toggleShowRobots()}
                      checked={this.props.showRobots}
                      style={{ marginLeft: 2.5 }}
                    />
                  }
                />
              </FormControl>

              <FormControl>
                <FormControlLabel
                  label="Show Trucks"
                  control={
                    <CustomCheckbox onChange={() => this.props.toggleShowTrucks()} style={{ marginLeft: 2.5 }} />
                  }
                  checked={this.props.showTrucks}
                />
              </FormControl>
            </Grid>

            <Grid container justifyContent="center" style={{ padding: 10 }} size={12}>
              <Typography gutterBottom>Robot Refresh Rate</Typography>
              <Slider
                style={{ width: '80%' }}
                defaultValue={30}
                step={5}
                value={this.props.robotRefreshRate}
                valueLabelDisplay="auto"
                min={10}
                max={60}
                color="secondary"
                onChange={debounce((e, value) => {
                  this.props.updateRobotRefreshRate(value);
                }, 10)}
              />
            </Grid>

            {this.props.showDropoffs && (
              <Grid container size={12}>
                <Grid
                  container
                  style={{
                    flexDirection: 'row',
                    display: 'flex',
                    marginBottom: 20,
                  }}
                  justifyContent="center"
                  size={12}
                >
                  <TextField
                    label="Dropoff Name"
                    style={{ width: '25%' }}
                    onBlur={(event) => this.setState({ filterDropoffName: event.target.value })}
                  />
                  <Select
                    value={this.state.dropoffContainsFilter}
                    onChange={async (event) =>
                      this.setState({ dropoffContainsFilter: event.target.value as FilterName })
                    }
                    style={{ marginLeft: 25, marginRight: 25, width: '25%' }}
                    autoWidth
                  >
                    <MenuItem value="contains">contains</MenuItem>
                    <MenuItem value="!contain">!contain</MenuItem>
                  </Select>
                  <CustomCheckbox
                    checked={this.state.nameFilterDropoff}
                    onChange={() => this.setState({ nameFilterDropoff: !this.state.nameFilterDropoff })}
                    style={{ marginLeft: 2.5 }}
                  />
                </Grid>
              </Grid>
            )}

            <Grid container size={12}>
              {this.props.showJobs && (
                <Grid
                  container
                  style={{
                    flexDirection: 'row',
                    display: 'flex',
                    marginBottom: 20,
                  }}
                  justifyContent="center"
                  size={12}
                >
                  <TextField
                    label="Job Name"
                    style={{ width: '25%' }}
                    onBlur={(event) => this.setState({ filterTextName: event.target.value })}
                  />

                  <Select
                    value={this.state.filterName}
                    onChange={async (event) => this.setState({ filterName: event.target.value as FilterName })}
                    style={{ marginLeft: 25, marginRight: 25, width: '25%' }}
                    autoWidth
                  >
                    <MenuItem value="contains">contains</MenuItem>
                    <MenuItem value="!contain">!contain</MenuItem>
                  </Select>

                  <CustomCheckbox
                    checked={this.state.nameFilter}
                    onChange={() => this.updateFilter('nameFilter', !this.state.nameFilter)}
                    style={{ marginLeft: 2.5 }}
                  />
                </Grid>
              )}

              {(this.props.showJobs || this.props.showDropoffs) && (
                <Grid
                  container
                  style={{
                    flexDirection: 'row',
                    display: 'flex',
                    marginBottom: 20,
                  }}
                  justifyContent="center"
                  size={12}
                >
                  <TextField
                    label="Lab Name"
                    style={{ width: '25%' }}
                    onBlur={(event) => this.setState({ labNameFilterText: event.target.value })}
                  />

                  <Select
                    value={this.state.labNameFilterType}
                    onChange={async (event) => this.setState({ labNameFilterType: event.target.value as FilterName })}
                    style={{ marginLeft: 25, marginRight: 25, width: '25%' }}
                    autoWidth
                  >
                    <MenuItem value="contains">contains</MenuItem>
                    <MenuItem value="!contain">!contain</MenuItem>
                  </Select>

                  <CustomCheckbox
                    checked={this.state.labNameFilterEnabled}
                    onChange={() => this.updateFilter('labNameFilterEnabled', !this.state.labNameFilterEnabled)}
                    style={{ marginLeft: 2.5 }}
                  />
                </Grid>
              )}

              {this.props.showJobs && (
                <Grid
                  container
                  style={{
                    flexDirection: 'row',
                    display: 'flex',
                    marginBottom: 20,
                  }}
                  justifyContent="center"
                  size={12}
                >
                  <TextField
                    label="Test Package"
                    style={{ width: '25%' }}
                    onBlur={(event) => this.setState({ testPackageFilterText: event.target.value })}
                  />

                  <Select
                    value={this.state.testPackageFilterType}
                    onChange={async (event) =>
                      this.setState({ testPackageFilterType: event.target.value as FilterName })
                    }
                    style={{ marginLeft: 25, marginRight: 25, width: '25%' }}
                    autoWidth
                  >
                    <MenuItem value="contains">contains</MenuItem>
                    <MenuItem value="!contain">!contain</MenuItem>
                  </Select>

                  <CustomCheckbox
                    checked={this.state.testPackageFilterEnabled}
                    onChange={() => this.updateFilter('testPackageFilterEnabled', !this.state.testPackageFilterEnabled)}
                    style={{ marginLeft: 2.5 }}
                  />
                </Grid>
              )}

              {this.props.showJobs && (
                <Grid
                  container
                  style={{
                    flexDirection: 'row',
                    display: 'flex',
                    marginBottom: 20,
                  }}
                  justifyContent="center"
                  size={12}
                >
                  <TextField
                    label="Days Late"
                    style={{ width: '25%' }}
                    onBlur={(event) => {
                      this.setState({ filterTextLate: event.target.value });
                    }}
                    inputMode="numeric"
                  />
                  <Select
                    value={this.state.filterByLate}
                    onChange={(event) => this.updateFilter('filterByLate', event.target.value as FilterByLate)}
                    style={{ marginLeft: 25, marginRight: 25, width: '25%' }}
                    autoWidth
                  >
                    <MenuItem value=">">{'<'}</MenuItem>
                    <MenuItem value="<">{'>'}</MenuItem>
                    <MenuItem value="==">{'=='}</MenuItem>
                  </Select>
                  <CustomCheckbox
                    checked={this.state.lateFilter}
                    onChange={() => this.updateFilter('lateFilter', !this.state.lateFilter)}
                    style={{ marginLeft: 2.5 }}
                  />
                </Grid>
              )}

              {this.props.showJobs && (
                <Grid
                  container
                  style={{
                    flexDirection: 'row',
                    display: 'flex',
                    marginBottom: 20,
                  }}
                  justifyContent="center"
                  size={12}
                >
                  <FormControl style={{ marginLeft: 25, marginRight: 25, width: '50%' }}>
                    <FormControlLabel
                      label="Priority"
                      labelPlacement="start"
                      control={
                        <Select
                          value={this.state.filterByPriority}
                          onChange={(event) => this.updateFilter('filterByPriority', event.target.value as string)}
                          style={{ marginLeft: 25, width: '100%' }}
                          autoWidth
                        >
                          {_(this.props.unassigned)
                            .filter((jobOrDropoff) => jobOrDropoff.table === 'Jobs')
                            .map((job) => job.get('Priority OVRD'))
                            .uniq()
                            .map((priority) => (
                              <MenuItem key={priority || 'undefined'} value={priority}>
                                {priority}
                              </MenuItem>
                            ))
                            .value()}
                        </Select>
                      }
                    />
                  </FormControl>

                  <FormControl>
                    <CustomCheckbox
                      checked={this.state.usePriorityFilter}
                      onChange={() => this.updateFilter('usePriorityFilter', !this.state.usePriorityFilter)}
                      style={{ marginLeft: 2.5 }}
                    />
                  </FormControl>
                </Grid>
              )}
            </Grid>

            <Grid>
              <FormControl>
                <FormControlLabel
                  label="Group By"
                  labelPlacement="start"
                  control={
                    <Select
                      value={this.state.groupScheduledBy}
                      onChange={async (event) => {
                        const newValue = event.target.value as GroupScheduledBy;
                        this.setState({ groupScheduledBy: newValue });
                      }}
                      style={{ marginLeft: 10, marginRight: 10 }}
                      autoWidth
                    >
                      {groupScheduledByOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  }
                />
              </FormControl>
            </Grid>

            <CreateShift
              schedDate={this.state.schedDate}
              robot={this.state.robot}
              operator={this.state.operator}
              rig={this.state.rig}
              allRobots={this.props.allRobots}
              allOperators={this.state.allOperators}
              allRigs={this.props.allRigs}
              onSchedDateChange={(value) => this.setState({ schedDate: value })}
              onRobotChange={(value) => this.setState({ robot: value })}
              onOperatorChange={(value) => this.setState({ operator: value })}
              onRigChange={(value) => this.setState({ rig: value })}
              onCreateShift={this.createShift.bind(this)}
            />

            {this.props.showDropoffs && (
              <Grid
                container
                style={{
                  flexDirection: 'column',
                  display: 'flex',
                  marginBottom: 2,
                }}
                size={12}
              >
                <MyAccordion style={{ width: '100%' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body1" style={{ padding: 5 }}>{`Dropoffs -- ${dropoffs.length}`}</Typography>
                  </AccordionSummary>

                  <DropoffList list={dropoffs} updateZoomField={this.props.updateZoomField} />
                </MyAccordion>
              </Grid>
            )}
            {this.props.showJobs && (
              <Grid
                container
                style={{
                  flexDirection: 'column',
                  display: 'flex',
                }}
                size={12}
              >
                <Grid container style={{ marginBottom: 2 }} size={12}>
                  <MyAccordion style={{ width: '100%' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <FormControlLabel
                        onClick={(event) => event.stopPropagation()}
                        onFocus={(event) => event.stopPropagation()}
                        label={`Unassigned -- ${Math.round(totalOpsAcresUnassigned)} OA / ${Math.round(totalBoundaryAcresUnassigned)} BA -- ${unassigned.length} Jobs`}
                        control={
                          <CustomCheckbox
                            checked={this.props.selectedShift === 'Unassigned'}
                            style={{ marginRight: 5 }}
                            onChange={() => {
                              this.props.selectedShift !== 'Unassigned' && this.props.updateSelectedShift('Unassigned');
                            }}
                          />
                        }
                      />
                    </AccordionSummary>

                    <JobAndDropoffList list={unassigned} updateZoomField={this.props.updateZoomField} />
                  </MyAccordion>
                </Grid>

                <Typography style={{ padding: '10px 5px' }}>{`Total scheduled: ${scheduledCount}`}</Typography>

                <SchedulingGrouped
                  scheduledGrouped={scheduledGrouped}
                  hiddenScheduled={this.props.hiddenScheduled}
                  updateHiddenScheduled={this.props.updateHiddenScheduled}
                  selectedShift={this.props.selectedShift}
                  updateSelectedShift={this.props.updateSelectedShift}
                  updateZoomField={this.props.updateZoomField}
                  onRolloverShiftClick={() => this.setState({ rolloverModalOpen: true, jobRolloverFrom: undefined })}
                  onRolloverJobClick={(job: LocalAtRecord<JobsFieldSet>) =>
                    this.setState({ rolloverModalOpen: true, jobRolloverFrom: job })
                  }
                />
              </Grid>
            )}
          </Paper>
          <div
            id="dragger"
            onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => {
              this.handleMousedown(event);
            }}
            className={'dragger'}
          />
        </Grid>
        <Dialog
          open={this.state.rolloverModalOpen}
          onClose={() => this.setState({ rolloverModalOpen: false, jobRolloverFrom: undefined })}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: { sx: { borderRadius: 1 } },
          }}
        >
          <DialogContent>
            <ShiftRolloverForm
              availableShifts={Object.keys(this.state.availableShiftsByKeys)
                .filter((key) => key !== this.props.selectedShift)
                .map((key) => this.state.availableShiftsByKeys[key])}
              rolloverFromShift={this.state.availableShiftsByKeys[this.props.selectedShift]}
              shiftEntities={
                this.props.selectedShift && Array.isArray(scheduled[this.props.selectedShift])
                  ? scheduled[this.props.selectedShift]
                      .filter((item): item is LocalAtRecord<JobsFieldSet> =>
                        ['Jobs', 'Shift Dropoffs'].includes(item.table),
                      )
                      .filter((job: LocalAtRecord<JobsFieldSet>) => {
                        // If we rolling over the whole shift,
                        // include all its jobs
                        if (!this.state.jobRolloverFrom) {
                          return true;
                        }

                        const jobRolloverFromOrder = this.state.jobRolloverFrom.get('Order');

                        if (jobRolloverFromOrder === undefined) {
                          // If order of the job we're rolling over from is undefined,
                          // then do not include any jobs in the list
                          return false;
                        }

                        const jobOrder = job.get('Order');

                        if (jobOrder === undefined) {
                          // Jobs with order === undefined should be at the bottom based on jobsSortingFunction,
                          // so we will include them in the rollover list
                          return true;
                        }

                        return jobOrder >= jobRolloverFromOrder;
                      })
                  : []
              }
              onCompletion={async () => {
                await this.refreshJobsAndShifts();

                if (this._isMounted) {
                  this.setState({ rolloverModalOpen: false, jobRolloverFrom: undefined });
                }
              }}
              allRobots={this.props.allRobots}
              allOperators={this.state.allOperators}
              allRigs={this.props.allRigs}
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  }
}
